/**
 * Presence store — keyed by userId, fed by:
 *
 *   1. Initial REST seed (`presence.api.ts → list(userIds)`) — fills the
 *      store before the socket is up so UI doesn't flicker.
 *   2. Live Phoenix.Presence channel updates — `presence_state` on join +
 *      `presence_diff` thereafter (wired in `usePresenceSubscription`).
 *   3. `presence:offline_confirmed` after the 30s grace window —
 *      switches from "disconnected" to fully "offline" with last_seen.
 *
 * The shape matches the REST `Snapshot` contract (see
 * `inmeet_client/docs/api-contracts.md` — Presence Module).
 */

import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import { useAuthStore } from '@/features/auth/store/auth.store'

export type PresenceStatus = 'online' | 'away' | 'busy' | 'dnd' | 'offline'

export interface PresenceInfo {
  userId:        string
  status:        PresenceStatus
  manualStatus:  PresenceStatus | null
  customMessage: string | null
  /** When `customMessage` clears itself (ISO 8601); null/absent = never.
   *  The server clears the message at this time — this is carried so the
   *  status dialog can show the expiry it is editing, not to enforce it. */
  customMessageExpiresAt?: string | null
  inMeeting:     boolean
  lastSeenAt:    string | null
  /** This user turned "Show status" off: nothing about their presence
   *  (dot, label, last seen) should render while this is true. */
  hidden:        boolean
}

interface PresenceState {
  byId: Record<string, PresenceInfo>
  /**
   * Users this account has blocked. Their presence is never reported, whatever
   * is cached or arrives later.
   *
   * The store keeps last-known status until something replaces it, and blocking
   * makes the server *stop sending* — so the final value before the block, very
   * often `online`, simply sat there forever. Nothing was going to arrive to
   * correct it. Purging on block is not enough on its own either: a stale
   * `presence_state` snapshot already in flight, or a re-seed from the REST
   * list, would put the row straight back. Masking on read is the part that
   * cannot be raced.
   */
  blockedIds: Set<string>
  /**
   * Whether the blocked list has loaded at least once since the current
   * identity signed in. Starts `false` on every fresh load.
   *
   * A fresh page paints presence from the REST seed before
   * `useBlockedContacts` has fetched the blocked list even once, so for that
   * window `blockedIds` is empty, not "known empty": a blocked contact looks
   * online until the fetch lands. While this is `false`, the masked selectors
   * below hide every OTHER user the same way they hide a known-blocked one, so
   * nothing paints before the real answer is in. `setBlockedIds` sets this on
   * every call, including an empty list, so the hold never outlives the load.
   */
  blockedReady: boolean
  setMany:   (rows: PresenceInfo[]) => void
  setOne:    (info: PresenceInfo) => void
  patchOne:  (userId: string, patch: Partial<PresenceInfo>) => void
  markOffline: (userId: string, lastSeenAt: string) => void
  removeMany:  (userIds: string[]) => void
  /** Replace the blocked set, drop any presence already held for them, and
   *  mark the blocked list ready. */
  setBlockedIds: (userIds: string[]) => void
  /**
   * Fail-open escape hatch for `useBlockedContacts`: after the blocked list
   * has failed to load a few times in a row, stop holding presence so the
   * app doesn't show everyone as offline for the rest of the session. Marks
   * ready without touching whatever (possibly empty) set is already held;
   * the caller keeps retrying in the background and `setBlockedIds` takes
   * over normally once a load succeeds.
   */
  markBlockedReadyFailOpen: () => void
  clear: () => void
}

// Memoize placeholders so `usePresence` returns a STABLE reference when the
// user isn't in the store — otherwise Zustand sees a fresh object every
// render and triggers an infinite re-render loop.
const placeholderCache = new Map<string, PresenceInfo>()
const offlinePlaceholder = (userId: string): PresenceInfo => {
  let cached = placeholderCache.get(userId)
  if (!cached) {
    cached = {
      userId,
      status:        'offline',
      manualStatus:  null,
      customMessage: null,
      inMeeting:     false,
      lastSeenAt:    null,
      hidden:        false,
    }
    placeholderCache.set(userId, cached)
  }
  return cached
}

export const usePresenceStore = create<PresenceState>((set) => ({
  byId: {},
  blockedIds: new Set<string>(),
  blockedReady: false,

  setMany: (rows) =>
    set((state) => ({
      byId: rows.reduce<Record<string, PresenceInfo>>(
        (acc, row) => {
          acc[row.userId] = row
          return acc
        },
        { ...state.byId },
      ),
    })),

  setOne: (info) =>
    set((state) => {
      const prev = state.byId[info.userId]
      // Keep a last-seen we already know when the incoming row carries none.
      //
      // `aggregate()` always returns `lastSeenAt: null` — it reads live metas,
      // which say nothing about when someone was last here. So a peer going
      // offline used to blank their last-seen and show a bare "offline" until
      // `presence:offline_confirmed` arrived up to 30 s later (the grace
      // window) — or never, if this client wasn't watching by then. The value
      // is only ever replaced by a real one, and a reconnect legitimately
      // clears it via `markOffline`/a fresh REST seed.
      const merged =
        info.lastSeenAt == null && prev?.lastSeenAt != null
          ? { ...info, lastSeenAt: prev.lastSeenAt }
          : info
      return { byId: { ...state.byId, [info.userId]: merged } }
    }),

  patchOne: (userId, patch) =>
    set((state) => {
      const prev = state.byId[userId] ?? offlinePlaceholder(userId)
      return { byId: { ...state.byId, [userId]: { ...prev, ...patch } } }
    }),

  markOffline: (userId, lastSeenAt) =>
    set((state) => {
      const prev = state.byId[userId] ?? offlinePlaceholder(userId)
      // A hidden row has no last-seen to gain: the grace timer firing after
      // the contact hid their status must not hand it one anyway.
      if (prev.hidden) return state
      return {
        byId: { ...state.byId, [userId]: { ...prev, status: 'offline', lastSeenAt } },
      }
    }),

  removeMany: (userIds) =>
    set((state) => {
      const next = { ...state.byId }
      for (const id of userIds) delete next[id]
      return { byId: next }
    }),

  setBlockedIds: (userIds) =>
    set((state) => {
      const blockedIds = new Set(userIds)
      // Drop what is already held, so the blocked rows are not sitting in the
      // store waiting to be read by anything that bypasses the selectors.
      const next = { ...state.byId }
      for (const id of blockedIds) delete next[id]
      return { blockedIds, byId: next, blockedReady: true }
    }),

  markBlockedReadyFailOpen: () =>
    set((state) => (state.blockedReady ? state : { blockedReady: true })),

  clear: () => set({ byId: {}, blockedIds: new Set<string>(), blockedReady: false }),
}))

/**
 * Is this id the signed-in user's own presence row?
 *
 * Keyed on `auth.presenceId`, the same per-org chat/presence identity every
 * other presence lookup in this codebase uses (see `useSelfPresence`,
 * `useConversationOnline`). Self can never be blocked, so this only matters
 * for the `blockedReady` hold below: without it, a member list that renders
 * a row for every participant (including the viewer) would show the signed-in
 * user as offline for the brief window before their own blocked list loads.
 */
const isSelfId = (userId: string): boolean => {
  const selfId = useAuthStore.getState().presenceId
  return selfId != null && selfId === userId
}

/** What a masked id reads as: the same shape as a contact who turned off
 *  "show my status", so the UI shows nothing at all (no dot, no Offline, no
 *  last seen, no status message) rather than claiming they are offline. */
const hiddenPlaceholderCache = new Map<string, PresenceInfo>()
const hiddenPlaceholder = (userId: string): PresenceInfo => {
  let cached = hiddenPlaceholderCache.get(userId)
  if (!cached) {
    cached = { ...offlinePlaceholder(userId), hidden: true }
    hiddenPlaceholderCache.set(userId, cached)
  }
  return cached
}

/** True when a selector should hide this id's real presence: it is blocked,
 *  or the blocked list has not loaded yet and the id is not the viewer. */
const isMasked = (s: PresenceState, userId: string): boolean =>
  !isSelfId(userId) && (s.blockedIds.has(userId) || !s.blockedReady)

/** Stable selector for a single user — returns the row or an offline placeholder.
 *  A blocked user reads as hidden (nothing shown). So does any other user while
 *  `blockedReady` is still false. The viewer's own row is exempt either way
 *  (see `isSelfId`). */
export const selectPresence = (userId: string) => (s: PresenceState): PresenceInfo =>
  isMasked(s, userId) ? hiddenPlaceholder(userId) : s.byId[userId] ?? offlinePlaceholder(userId)

/** Hook variant — re-renders only when *this user's* slice changes. */
export const usePresence = (userId: string): PresenceInfo =>
  usePresenceStore(selectPresence(userId))

/** Bulk variant. Returns rows in the same order as `userIds`.
 *  `useShallow` keeps the array reference stable when every element is the
 *  same as before — required to avoid an infinite re-render. */
export const usePresenceBulk = (userIds: string[]): PresenceInfo[] =>
  usePresenceStore(
    useShallow((s) =>
      userIds.map((id) => (isMasked(s, id) ? hiddenPlaceholder(id) : s.byId[id] ?? offlinePlaceholder(id))),
    ),
  )
